import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Contact, CreditCard, Truck } from 'lucide-react';
import { get, post } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { FormActions, FormScreen, FormSection } from '@/components/ui/form-screen';
import { RecordState } from '@/components/ui/record-state';
import { Alert, Checkbox, Field, Input } from '@/components/ui/primitives';
import { PhoneText } from '@/components/ui/phone-text';
import { useToast } from '@/components/ui/toast';
import { safeReturnTo } from '../opd/BookAppointmentPage';
import type { SupplierRow } from './InventoryPage';

const SUPPLIERS_TAB = '/inventory?tab=suppliers';

function supplierCode(name: string): string {
  const words = name.trim().toUpperCase().split(/[^A-Z0-9]+/).filter(Boolean);
  if (words.length === 0) return '';
  const joined = words.length === 1 ? words[0] : words.map((w) => w.slice(0, 4)).join('');
  return joined.slice(0, 16);
}

function initialForm(supplier: SupplierRow | null) {
  return {
    code: supplier?.code ?? '',
    codeTouched: Boolean(supplier?.code),
    name: supplier?.name ?? '',
    contactPerson: supplier?.contactPerson ?? '',
    phone: supplier?.phone ?? '',
    email: supplier?.email ?? '',
    address: supplier?.address ?? '',
    taxNumber: supplier?.taxNumber ?? '',
    paymentTermsDays: String(supplier?.paymentTermsDays ?? 30),
    isActive: supplier?.isActive ?? true,
  };
}

export function SupplierPage() {
  const { supplierId } = useParams<{ supplierId: string }>();
  const [params] = useSearchParams();
  const location = useLocation();
  const returnTo = safeReturnTo(params.get('returnTo'), SUPPLIERS_TAB);
  const carried = (location.state as { supplier?: SupplierRow } | null)?.supplier;
  const fromState = carried && carried.id === supplierId ? carried : null;

  const lookup = useQuery({
    queryKey: ['suppliers'],
    queryFn: () => get<SupplierRow[]>('/pharmacy/suppliers'),
    enabled: Boolean(supplierId) && fromState === null,
  });

  if (!supplierId) return <SupplierForm key="new" supplier={null} returnTo={returnTo} />;
  const supplier = fromState ?? lookup.data?.find((row) => row.id === supplierId) ?? null;
  if (supplier) return <SupplierForm key={supplier.id} supplier={supplier} returnTo={returnTo} />;
  return <RecordState error={lookup.error} missing={lookup.isSuccess} />;
}

function SupplierForm({ supplier, returnTo }: { supplier: SupplierRow | null; returnTo: string }) {
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [initial] = useState(() => initialForm(supplier));
  const [form, setForm] = useState(initial);
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () =>
      post('/pharmacy/suppliers', {
        ...(supplier?.id ? { id: supplier.id } : {}),
        code: form.code,
        name: form.name,
        contactPerson: form.contactPerson || undefined,
        phone: form.phone || undefined,
        email: form.email || undefined,
        address: form.address || undefined,
        taxNumber: form.taxNumber || undefined,
        paymentTermsDays: Number(form.paymentTermsDays || 30),
        isActive: form.isActive,
      }),
    onSuccess: () => {
      toast.success(supplier?.id ? tr('pharmacy', 'supplier_updated') : tr('pharmacy', 'supplier_added'), tr('pharmacy', 'goods_can_now_be_received_against_them'));
      void queryClient.invalidateQueries({ queryKey: ['suppliers'] });
      navigate(returnTo, { replace: true });
    },
    onError: (caught) => setError(describeError(caught)),
  });

  const valid = form.code.trim().length >= 2 && form.name.trim().length >= 2;
  const dirty = !save.isSuccess && JSON.stringify(form) !== JSON.stringify(initial);

  return (
    <FormScreen
      title={supplier?.id ? tr('pharmacy', 'edit_supplier') : tr('pharmacy', 'add_supplier')}
      description={tr('pharmacy', 'who_the_stock_is_bought_from_every_goods')}
      eyebrow={tr('pharmacy', 'inventory')}
      icon={Truck}
      backTo={returnTo}
      dirty={dirty}
      onSubmit={(event) => {
        event.preventDefault();
        if (!valid) return;
        setError(null);
        save.mutate();
      }}
    >
      <FormSection title={tr('admin', 'identity')} icon={Truck}>
        <Field label={tr('pharmacy', 'name')} required>
          <Input
            value={form.name}
            onChange={(e) => {
              const name = e.target.value;
              setForm((current) => ({
                ...current,
                name,
                code: current.codeTouched ? current.code : supplierCode(name),
              }));
            }}
          />
        </Field>
        <Field label={tr('pharmacy', 'code')} required hint={tr('pharmacy', 'made_from_the_name_change_it_if_you_keep_your')}>
          <Input
            value={form.code}
            onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase().replace(/[^A-Z0-9_-]/g, ''), codeTouched: true })}
            placeholder={tr('pharmacy', 'medipak')}
          />
        </Field>
      </FormSection>

      <FormSection title={tr('pharmacy', 'contact')} icon={Contact}>
        <Field label={tr('pharmacy', 'contact_person')}>
          <Input value={form.contactPerson} onChange={(e) => setForm({ ...form, contactPerson: e.target.value })} />
        </Field>
        <Field label={tr('pharmacy', 'phone')}>
          <PhoneText value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
        </Field>
        <Field label={tr('pharmacy', 'email')}>
          <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
        </Field>
        <Field label={tr('pharmacy', 'address')}>
          <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
        </Field>
      </FormSection>

      <FormSection title={tr('pharmacy', 'payment')} icon={CreditCard}>
        <Field label={tr('pharmacy', 'tax_number')}>
          <Input value={form.taxNumber} onChange={(e) => setForm({ ...form, taxNumber: e.target.value })} />
        </Field>
        <Field label={tr('pharmacy', 'payment_terms_days')}>
          <Input type="number" min={0} max={365} value={form.paymentTermsDays} onChange={(e) => setForm({ ...form, paymentTermsDays: e.target.value })} />
        </Field>
        <Checkbox
          className="sm:col-span-2"
          checked={form.isActive}
          onCheckedChange={(checked) => setForm({ ...form, isActive: checked })}
          label={tr('pharmacy', 'still_buying_from_them')}
        />
      </FormSection>

      {error && <Alert variant="destructive">{error}</Alert>}

      <FormActions
        submitLabel={supplier?.id ? tr('pharmacy', 'save_changes') : tr('pharmacy', 'add_supplier')}
        loading={save.isPending}
        incomplete={!valid}
        cancelTo={returnTo}
      />
    </FormScreen>
  );
}
